<script setup lang="ts">
import debounce from "lodash.debounce";
import { trackRudderstackImageEdited } from "common/utils/rudderstack/tracker";
import { useDtfStore } from "../store";
import type { Standard, EnhancementTier } from "../types";
import { Tabs, TabButton } from "./components/Tabs";
import PrintAsIs from "./PrintAsIs/PrintAsIs.vue";
import Basic from "./Basic/Basic.vue";
import PrintPerfect from "./PrintPerfect/PrintPerfect.vue";
import Custom from "./Custom/Custom.vue";

const props = defineProps<{
  index: number;
  artwork: Standard.Artwork;
  reorderEnabled: boolean;
  enableAbMonetizationUiV2: boolean;
}>();

const store = useDtfStore();

const debouncedToggleEnhancement = debounce(async (section: EnhancementTier) => {
  if (!props.artwork?.id) {
    return;
  }

  const enableEnhancement = section !== "print_original";

  await store.updateEnableEnhancement({
    artworkId: props.artwork.id,
    enableEnhancement,
    enhancementTier: section
  });
}, 100);

function setActiveConfigSection(section: EnhancementTier) {
  if (props.artwork.enhancementCompleted) {
    debouncedToggleEnhancement(section);
  } else {
    store.updateArtworkValuesById({
      id: props.artwork.id,
      enhancementTier: section,
      pricing: store.cachedArtworkPrices[section] || {}
    });
  }

  trackRudderstackImageEdited({
    // eslint-disable-next-line no-nested-ternary
    product_line: store.sticker ? "sticker" : store.isProColor ? "pro color" : "dtf",
    artwork: {
      ...props.artwork,
      enhancementTier: section
    },
    edit_type: section === "print_original" ? "Print as uploaded" : "Jiffy Print Perfect",
    artworks_count: store.artworks.length,
    location: "pdp",
    pre_cut_transfers: store.preCut
  });
}

function setPrintPerfectSection() {
  setActiveConfigSection("full_enhancements");
}
</script>

<template>
  <Tabs class="artwork-quality-config-panel">
    <template #buttons>
      <TabButton
        :active="artwork.enhancementTier === 'print_original'"
        :icon="enableAbMonetizationUiV2 ? undefined : 'printer'"
        @click="setActiveConfigSection('print_original')"
      >
        Print As Is
      </TabButton>

      <TabButton
        :active="artwork.enhancementTier === 'basic'"
        :icon="enableAbMonetizationUiV2 ? undefined : 'star-4'"
        @click="setActiveConfigSection('basic')"
      >
        Basic
      </TabButton>

      <TabButton
        :active="artwork.enhancementTier === 'full_enhancements'"
        :icon="enableAbMonetizationUiV2 ? 'stars-corners' : 'stars'"
        @click="setActiveConfigSection('full_enhancements')"
      >
        PrintPerfect
      </TabButton>

      <TabButton
        :active="artwork.enhancementTier === 'custom'"
        :icon="enableAbMonetizationUiV2 ? undefined : 'stars-4'"
        @click="setActiveConfigSection('custom')"
      >
        Expert Edit
      </TabButton>
    </template>

    <PrintAsIs
      v-if="artwork.enhancementTier === 'print_original'"
      :key="artwork.enhancementTier"
      :enable-ab-monetization-ui-v2="enableAbMonetizationUiV2"
      @print-perfect-click="setPrintPerfectSection"
    />
    <Basic
      v-if="artwork.enhancementTier === 'basic'"
      :key="artwork.enhancementTier"
      :artwork="artwork"
      :enable-ab-monetization-ui-v2="enableAbMonetizationUiV2"
      @print-perfect-click="setPrintPerfectSection"
    />
    <PrintPerfect
      v-if="artwork.enhancementTier === 'full_enhancements'"
      :key="artwork.enhancementTier"
      :artwork="artwork"
      :reorder-enabled="reorderEnabled"
      :enable-ab-monetization-ui-v2="enableAbMonetizationUiV2"
    />
    <Custom
      v-if="artwork.enhancementTier === 'custom'"
      :key="artwork.enhancementTier"
      :artwork="artwork"
      :index="index"
      :reorder-enabled="reorderEnabled"
      :enable-ab-monetization-ui-v2="enableAbMonetizationUiV2"
    />
  </Tabs>
</template>

<style scoped lang="scss">
@import "./ArtworkQualityConfigPanel.scss";

.artwork-quality-config-panel {
  padding: 0;
  container: #{$container-name} / inline-size;
}
</style>
